import { useEffect, useState } from 'react';
import { loadStudioImage } from '@/hooks/useStudioImage';

const K = 800 / 600;
export const GOOD_DPI = 150, OK_DPI = 100;
export const QUALITY = { good: { label: 'Gut', color: '#2f9e44' }, ok: { label: 'Geht so', color: '#f59f00' }, low: { label: 'Zu unscharf', color: '#e03131' } };

export const garmentColor = (product, meta = {}) => (product.colors || []).find(c => c.id === meta.garment_color) || (product.colors || [])[0] || null;
export const viewDef = (product, view) => view && view !== 'front' ? (product.views || []).find(v => v.id === view) : null;
export const unitsPerMmFor = (product, view) => { const v = viewDef(product, view); return v ? v.area.w / K / v.area_width_mm : product.area.w / K / (product.area_width_mm || 220); };

const lum = hex => { const n = parseInt(hex.slice(1), 16), c = [n >> 16, (n >> 8) & 255, n & 255].map(v => { v /= 255; return v <= .03928 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4; }); return .2126 * c[0] + .7152 * c[1] + .0722 * c[2]; };
export const contrastRatio = (a, b) => { const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p); return (x + .05) / (y + .05); };
export const lowContrast = (color, garmentHex) => !!color && !!garmentHex && contrastRatio(color, garmentHex) < 1.6;
// Invisible only when fill and every outline vanish on the garment; gradients are never flagged.
export const faintOnGarment = (o, garmentHex) => !(o.meta?.gradient?.length > 1) && [o.meta?.color || '#111111', o.textStroke > 0 && o.textStrokeColor, o.meta?.text_stroke2 > 0 && o.meta.text_stroke2_color].filter(Boolean).every(c => lowContrast(c, garmentHex));

const pixels = new Map(), listeners = new Set();
const measure = id => {
  if (!id || pixels.has(id)) return;
  pixels.set(id, null);
  loadStudioImage(id).then(url => { const img = new Image(); img.onload = () => { pixels.set(id, img.naturalWidth); listeners.forEach(fn => fn()); }; img.src = url; }).catch(() => pixels.delete(id));
};
export const printedAsset = o => o.backgroundRemoved && o.cutoutAssetId ? o.cutoutAssetId : o.assetId;

// Effective print resolution of a textile photo at its real printed width.
export const printQuality = (o, product) => {
  const px = pixels.get(printedAsset(o));
  if (!px) return null;
  const perMm = unitsPerMmFor(product, o.meta?.view), widthMm = o.baseW * (o.scale || 1) * Math.max(o.meta?.stretch_x || 1, o.meta?.stretch_y || 1) / perMm, source = px * (o.crop?.w ?? 1);
  const dpi = Math.round(source / (widthMm / 25.4)), level = dpi >= GOOD_DPI ? 'good' : dpi >= OK_DPI ? 'ok' : 'low';
  return { dpi, level, widthCm: widthMm / 10, goodWidthCm: source / GOOD_DPI * 2.54, goodScale: (o.scale || 1) * Math.min(1, dpi / GOOD_DPI) };
};

export const usePrintQuality = (objects, product) => {
  const [, tick] = useState(0);
  const images = product.kind === 'textile' ? objects.filter(o => o.type === 'image' && o.assetId && !o.placeholder) : [];
  const ids = images.map(printedAsset).join('|');
  useEffect(() => { const fn = () => tick(n => n + 1); listeners.add(fn); ids.split('|').forEach(measure); return () => listeners.delete(fn); }, [ids]);
  return Object.fromEntries(images.map(o => [o.id, printQuality(o, product)]).filter(([, q]) => q));
};

// Hints shown before ordering: blurry photos and colours that vanish on the garment.
export const textileWarnings = (objects, product, meta, quality) => {
  if (product.kind !== 'textile') return [];
  const garment = garmentColor(product, meta), side = o => viewDef(product, o.meta?.view)?.name || 'Vorderseite', out = [];
  for (const o of objects.filter(x => x.visible)) {
    const q = quality[o.id];
    if (o.type === 'image' && q && q.level !== 'good') out.push({ id: o.id, level: q.level, image: o, goodScale: q.goodScale, text: `„${o.name}“ (${side(o)}): Druckqualität ${QUALITY[q.level].label.toLowerCase()} · ${q.dpi} dpi. Für ein scharfes Ergebnis höchstens ${q.goodWidthCm.toFixed(1)} cm breit.` });
    if (o.type !== 'image' && garment && faintOnGarment(o, garment.hex)) out.push({ id: o.id, level: 'ok', text: `„${o.name}“ (${side(o)}): Farbe ist auf ${garment.name} kaum sichtbar.` });
  }
  return out;
};
